/**
 * Global drag-to-move for every NON-BaseModal dialog in the app. The legacy
 * modal population comes in several markup shapes:
 *   - `<div class="modal show d-block">` wrappers (~22 components)
 *   - `<div class="modal-backdrop">` wrappers (~18 components — e.g.
 *     BOQImportModal "Import Takeoff to BOQ", projects TaskModal)
 *   - `.modal-overlay > .modal-container` (CatalogueManagement, BulkPrice,
 *     RecipeEditor, FormulaBuilder, OrderPreview)
 *   - `.modal-overlay > .modal-panel` (QuoteCreateModal)
 *   - `.modal-overlay > <unclassed box>` (OrdersView "New Draft Order")
 *   - fully custom overlays (EstimateReviewModal's `.er-overlay/.er-header`,
 *     DocumentPreviewModal's `.document-preview-overlay`)
 *
 * One delegated pointerdown listener makes all of them movable by their
 * header row, with the same behaviour as BaseModal's built-in drag:
 * viewport-clamped, mouse/pen only (touch keeps native behaviour), header
 * buttons/inputs untouched. Because these modals are `v-if`-mounted, the
 * inline transform dies with the element, so position naturally resets on
 * every open.
 *
 * BaseModal is NOT affected: its header carries `rw-modal-header`, which is
 * explicitly excluded — its own built-in drag handles it (no double-drag).
 *
 * As modals migrate to BaseModal this helper simply stops applying to them;
 * delete the whole file once the last legacy modal is gone.
 */
const HEADER_SELECTOR = '.modal-header, .er-header';
const DIALOG_SELECTOR = '.modal-dialog, .er-dialog, .modal-container, .modal-panel';
const OVERLAY_SELECTOR =
  '.modal, .modal-backdrop, .modal-overlay, .er-overlay, .document-preview-overlay';

/**
 * The element the drag transform is applied to. Fast path: a known dialog
 * class on the header's ancestor chain. Fallback (unclassed boxes like
 * OrdersView's `.new-order-modal`): the ancestor of the header that is a
 * DIRECT child of the overlay — that's the visual dialog box in every
 * flex-centred overlay in this app.
 */
function findDialog(header: HTMLElement): HTMLElement | null {
  const known = header.closest<HTMLElement>(DIALOG_SELECTOR);
  if (known) return known;
  const overlay = header.closest<HTMLElement>(OVERLAY_SELECTOR);
  if (!overlay) return null;
  let el: HTMLElement | null = header;
  while (el && el.parentElement !== overlay) el = el.parentElement;
  return el && el !== overlay ? el : null;
}
export function installLegacyModalDrag(): void {
  // Cursor affordance + no text selection while grabbing the title bar.
  const style = document.createElement('style');
  style.textContent =
    '.modal-header:not(.rw-modal-header), .er-header { cursor: move; user-select: none; touch-action: none; }';
  document.head.appendChild(style);

  document.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    const target = e.target as HTMLElement;
    const header = target.closest<HTMLElement>(HEADER_SELECTOR);
    if (!header || header.classList.contains('rw-modal-header')) return;
    // Buttons/links/inputs in the header (incl. the X) keep their own behaviour.
    if (target.closest('button, a, input, select, textarea, [role="button"]')) return;
    const dialog = findDialog(header);
    if (!dialog) return;

    const m = /translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/.exec(dialog.style.transform || '');
    const baseTx = m ? parseFloat(m[1]) : 0;
    const baseTy = m ? parseFloat(m[2]) : 0;
    const startX = e.clientX;
    const startY = e.clientY;
    const r = dialog.getBoundingClientRect();
    // The dialog's rect with the current translate removed — the fixed
    // reference the viewport clamp works against.
    const baseLeft = r.left - baseTx;
    const baseTop = r.top - baseTy;
    const baseRight = r.right - baseTx;

    const onMove = (ev: PointerEvent) => {
      let x = baseTx + (ev.clientX - startX);
      let y = baseTy + (ev.clientY - startY);
      // Keep a graspable part on-screen: ≥120px horizontally, header never
      // above the top edge nor below the bottom 60px.
      x = Math.min(Math.max(x, -(baseRight - 120)), window.innerWidth - baseLeft - 120);
      y = Math.min(Math.max(y, -baseTop), window.innerHeight - baseTop - 60);
      dialog.style.transform = `translate(${x}px, ${y}px)`;
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    e.preventDefault(); // no text selection while dragging
  });
}
